<template>
    <div>
        <v-select
            v-if="!buttons && !autocomplete && !iterator"
            :items="locations"
            :value="multiple ? value : value[0]"
            :return-object="!idOnly"
            :disabled="disabled"
            :multiple="multiple"
            :rules="rules"
            item-text="name"
            item-value="id"
            prepend-inner-icon="storefront"
            outlined
            dense
            :hide-details="hideDetails"
            label="Location"
            v-on:input="$emit('input', multiple ? $event : [$event])"
        />
        <v-list v-if="buttons"
            ><v-list-item
                v-for="location in locations"
                :key="location.id"
            >
                <SoliAvatarBtn
                    :avatar="location.alt_image ? `${location.alt_image}/m/80x80` : '/musicologie-m-white.png'"
                    :title="location.name"
                    :loading="location.id == value.id ? loading : false"
                    :trueDisabled="value?.id == location.id"
                    @click="$emit('input', location)"
                /> </v-list-item
        ></v-list>
        <v-autocomplete
            v-if="autocomplete && !buttons && !iterator"
            :value="value"
            :items="locations"
            :search-input.sync="search"
            :multiple="multiple"
            :loading="loading"
            :disabled="disabled"
            :rules="rules"
            :hide-details="hideDetails"
            prepend-inner-icon="storefront"
            :return-object="!idOnly"
            autocomplete
            outlined
            :autofocus="autofocus"
            dense
            chips
            item-value="id"
            item-text="name"
            no-data-text="No locations found"
            :label="multiple ? 'Locations' : 'Location'"
            class="soli-blue-text single-line-selection"
            @input="$emit('input', $event)"
            @keydown.enter.prevent
        >
            <template v-slot:item="data">
                <v-list-item-avatar>
                    <img :src="data.item.alt_image ? `${data.item.alt_image}/m/80x80` : '/musicologie-m-white.png'" />
                </v-list-item-avatar>
                <v-list-item-content
                    :class="{ 'disabled-item': data.item.disabled }"
                    class="max-w-min"
                >
                    <v-list-item-title>{{ data.item.name }}</v-list-item-title>
                    <v-list-item-subtitle>{{ data.item.address }}</v-list-item-subtitle>
                    <v-list-item-subtitle>{{ data.item.phone }}</v-list-item-subtitle>
                </v-list-item-content>
            </template>
            <template v-slot:selection="{ attrs, item, index, parent }">
                <span v-if="parent.selectedItems.length > 2 && parent.selectedItems.length === locations.length">
                    <v-chip
                        v-if="index === 0"
                        v-bind="attrs"
                        small
                        close
                        class="mx-0 mr-1"
                        @click:close="$emit('input', multiple ? [] : null)"
                        >All Locations</v-chip
                    >
                </span>
                <span v-else>
                    <v-chip
                        v-if="index < 2"
                        v-bind="attrs"
                        small
                        class="mx-0 mr-1"
                    >
                        {{
                            item.name.length > 10 && parent.selectedItems.length > 1
                                ? item.name.substring(0, 7) + '...'
                                : item.name
                        }}
                    </v-chip>
                    <v-chip
                        v-if="index === 2"
                        v-bind="attrs"
                        small
                        class="mx-0 mr-1"
                        >{{ parent.selectedItems.length - 2 }} more</v-chip
                    >
                </span>
            </template>
        </v-autocomplete>
        <v-data-iterator
            v-if="iterator && !autocomplete && !buttons"
            :items="searchLocations"
            disable-pagination
            hide-default-footer
        >
            <template v-slot:header
                ><v-sheet style="position: sticky; top: 0px; z-index: 10"
                    ><SoliText
                        v-model="search"
                        :loading="loading"
                        :autofocus="autofocus"
                        fullRound
                        label="Search Locations"
                        icon="storefront"
                        hideDetails
                        class="pa-3"
                        @keydown.enter="searchLocations[0].id ? $emit('input', searchLocations[0]) : null"
                    /> </v-sheet
            ></template>
            <template v-slot:default="{ items }">
                <v-list
                    dense
                    class="soli-round mx-3 divide-y overflow-auto py-0"
                >
                    <v-list-item
                        v-for="(item, idx) in items"
                        :key="idx"
                        :input-value="!idx"
                        color="primary"
                        :class="{
                            'contrast--text': value.id !== item.id,
                        }"
                        class="location-list-item"
                        @click="$emit('input', item)"
                    >
                        <v-list-item-avatar size="56">
                            <img :src="item.alt_image ? `${item.alt_image}/m/80x80` : '/musicologie-m-white.png'" />
                        </v-list-item-avatar>
                        <v-list-item-content :class="{ 'contrast--text': !!idx }">
                            <v-list-item-title class="font-weight-bold">{{ item.name }}</v-list-item-title>
                            <v-list-item-subtitle>{{ item.phone }}</v-list-item-subtitle>
                            <v-list-item-subtitle>{{ item.address }}</v-list-item-subtitle>
                            <v-list-item-subtitle v-if="moment(new Date(item.opening_date)).isAfter(moment())">{{
                                `Opening: ${globalMomentDate(item.opening_date)}`
                            }}</v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action>
                            <v-icon
                                v-if="value.id === item.id"
                                small
                                color="primary"
                                >my_location</v-icon
                            ></v-list-item-action
                        >
                    </v-list-item>
                </v-list>
            </template></v-data-iterator
        >
    </div>
</template>

<script>
import GlobalMoment from '@/Mixins/GlobalMoment';

export default {
    props: {
        locations: {
            type: Array,
            default: () => [],
        },
        buttons: {
            type: Boolean,
            default: false,
        },
        iterator: {
            type: Boolean,
            default: false,
        },
        autocomplete: {
            type: Boolean,
            default: false,
        },
        rules: {
            type: Array,
            default: () => [],
        },
        loadLocations: {
            type: Boolean,
            default: false,
        },
        value: {
            type: [Object, Array],
            default: () => [],
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        idOnly: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        autofocus: {
            type: Boolean,
            default: false,
        },
        blueShadow: {
            type: Boolean,
            default: false,
        },
        hideDetails: {
            type: [Boolean, String],
            default: 'auto',
        },
    },
    mixins: [GlobalMoment],
    data: function () {
        return {
            search: '',
            //select: this.loadLocations ? this.locations : null,
        };
    },
    methods: {
        handleArrowNavigation(event) {
            const focusableElements = Array.from(document.querySelectorAll('.location-list-item')).sort(
                (a, b) => a.tabIndex - b.tabIndex
            );

            const currentIndex = focusableElements.indexOf(document.activeElement);

            if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
                event.preventDefault();
                const nextIndex = (currentIndex + 1) % focusableElements.length;
                focusableElements[nextIndex].focus();
            }

            if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
                event.preventDefault();
                const prevIndex = (currentIndex - 1 + focusableElements.length) % focusableElements.length;
                focusableElements[prevIndex].focus();
            }
        },
    },
    computed: {
        searchLocations() {
            if (!this.search) {
                return this.locations;
            }
            return this.locations.slice().sort((a, b) => {
                const searchLower = this.search.toLowerCase();
                const aMatches = a.name.toLowerCase().includes(searchLower);
                const bMatches = b.name.toLowerCase().includes(searchLower);
                if (aMatches && !bMatches) {
                    return -1;
                }
                if (!aMatches && bMatches) {
                    return 1;
                }
                return 0;
            });
        },
    },
};
</script>

<style scoped>
::v-deep .single-line-selection .v-select__selections {
    flex-wrap: nowrap;
}
</style>
